/**
 * Columna "Next activity" (OpenSpec 008 Fase 4): la nota programada futura más próxima de cada
 * registro. Usa el batch `POST /history/next-activity` (permisos en el WHERE del backend).
 *
 * Uso: `const next = useNextActivity("quote", ids)` con los ids de la página visible, y en la
 * columna `render: (_, r) => <NextActivityCell value={next[String(r.<id>)]} />`.
 */
import { useEffect, useMemo, useState } from "react";
import { Typography } from "antd";
import dayjs from "dayjs";
import { HistoryModule, historyNotesApi } from "@/services/history.api";

const { Text } = Typography;

/** entityId → scheduledFor ISO, para los registros con una programada futura. */
export function useNextActivity(
  module: HistoryModule,
  entityIds: (string | number)[],
): Record<string, string> {
  const [map, setMap] = useState<Record<string, string>>({});
  // Clave estable de la página visible (ordenada) para no re-pedir en cada render.
  const key = useMemo(
    () => Array.from(new Set(entityIds.map(String))).sort().join(","),
    [entityIds],
  );

  useEffect(() => {
    const ids = key ? key.split(",") : [];
    if (!ids.length) {
      setMap({});
      return;
    }
    let alive = true;
    historyNotesApi
      .nextActivity(ids.map((id) => ({ module, entityId: id })))
      .then((rows) => {
        if (alive) setMap(Object.fromEntries(rows.map((r) => [String(r.entityId), r.scheduledFor])));
      })
      .catch(() => alive && setMap({}));
    return () => {
      alive = false;
    };
  }, [module, key]);

  return map;
}

export function NextActivityCell({ value }: { value?: string }) {
  if (!value) return <Text type="secondary">—</Text>;
  const d = dayjs(value);
  return (
    <Text style={{ whiteSpace: "nowrap", color: "#15744b" }} title={d.format("DD/MM/YYYY HH:mm")}>
      {d.format("DD/MM/YY HH:mm")}
    </Text>
  );
}
